import { useQuery } from '@tanstack/react-query';
import { State } from '@/types/location';
import iso3166 from 'iso-3166-2';
import { debugLogger } from '@/utils/debugLogger';

// Type for iso-3166-2 subdivision data
interface Iso3166Subdivision {
  type: string;
  name: string;
}

interface Iso3166Country {
  name: string;
  code: string;
  sub: Record<string, Iso3166Subdivision>;
}

export const useGlobalStates = (countryCode: string | null) => {
  return useQuery({
    queryKey: ['global-states', countryCode],
    queryFn: (): State[] => {
      if (!countryCode) return [];
      
      debugLogger.debug('useGlobalStates', `Loading states for ${countryCode} from iso-3166-2...`);
      
      // Objeto JS estático - 225KB total, execução instantânea
      const country = iso3166.country(countryCode) as Iso3166Country | null;
      if (!country?.sub) {
        debugLogger.debug('useGlobalStates', `No subdivisions found for ${countryCode}`);
        return [];
      }
      
      // Map subdivisions to our format and sort alphabetically
      const states: State[] = Object.entries(country.sub)
        .map(([fullCode, data]) => ({
          code: fullCode.split('-')[1] || fullCode, // "US-CA" -> "CA", fallback to full code
          name: data.name,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      debugLogger.debug('useGlobalStates', `Loaded ${states.length} states for ${countryCode}`);
      return states;
    },
    enabled: !!countryCode,
    staleTime: Infinity, // Never refetch - static data
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};
